import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, ChevronLeft, ChevronRight, Loader2, CheckCircle, User, Building2, Mail, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
];

const CalendarBooking = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isDateSelectable = (day) => {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    return date >= today && dayOfWeek !== 0 && dayOfWeek !== 6;
  };

  const handleDateSelect = (day) => {
    if (!isDateSelectable(day)) return;
    setSelectedDate(new Date(year, month, day));
    setSelectedSlot(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const bookingData = {
        event_type: 'calendar_booking',
        source: 'calendar_widget',
        data: {
          ...form,
          date: selectedDate?.toISOString().split('T')[0],
          time: selectedSlot,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };
      await fetch(`${API}/api/nia/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });
      setSubmitted(true);
    } catch {}
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ name: '', email: '', company: '', phone: '', message: '' });
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={reset}>
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          onClick={e => e.stopPropagation()} data-testid="calendar-booking-modal">

          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-taxera-charcoal">Book a Demo</h2>
              <p className="text-xs text-slate-400">30-minute personalized walkthrough</p>
            </div>
            <button onClick={reset} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="booking-close">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>

          {submitted ? (
            <div className="px-6 py-12 text-center" data-testid="booking-success">
              <CheckCircle className="w-12 h-12 text-taxera-teal mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Booking Confirmed</h3>
              <p className="text-sm text-slate-500 mb-1">
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedSlot} CET
              </p>
              <p className="text-xs text-slate-400 mb-6">You'll receive a confirmation email shortly.</p>
              <button onClick={reset}
                className="px-6 py-2.5 bg-taxera-charcoal text-white rounded-full text-sm font-semibold hover:bg-slate-700 transition-colors"
                data-testid="booking-done">
                Done
              </button>
            </div>
          ) : step === 1 ? (
            <div className="p-6" data-testid="booking-step-1">
              {/* Calendar */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="booking-prev-month">
                  <ChevronLeft className="w-4 h-4 text-slate-500" />
                </button>
                <span className="text-sm font-bold text-taxera-charcoal">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="booking-next-month">
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-4">
                {dayNames.map(d => (
                  <div key={d} className="text-center text-2xs font-bold text-slate-400 py-1">{d}</div>
                ))}
                {Array.from({ length: firstDay }, (_, i) => (
                  <div key={`e-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const selectable = isDateSelectable(day);
                  const isSelected = selectedDate?.getDate() === day && selectedDate?.getMonth() === month && selectedDate?.getFullYear() === year;
                  const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
                  return (
                    <button key={day} onClick={() => handleDateSelect(day)}
                      disabled={!selectable}
                      className={`h-9 rounded-lg text-sm font-medium transition-all ${
                        isSelected ? 'bg-taxera-blue text-white' :
                        isToday ? 'bg-blue-50 text-taxera-blue font-bold' :
                        selectable ? 'hover:bg-slate-100 text-slate-700' :
                        'text-slate-200 cursor-not-allowed'
                      }`}
                      data-testid={`booking-day-${day}`}>
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div className="mt-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    Available Times — {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </h4>
                  <div className="grid grid-cols-4 gap-1.5">
                    {AVAILABLE_SLOTS.map(slot => (
                      <button key={slot} onClick={() => setSelectedSlot(slot)}
                        className={`py-2 rounded-lg text-xs font-semibold transition-all ${
                          selectedSlot === slot ? 'bg-taxera-teal text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`booking-slot-${slot}`}>
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedSlot && (
                <button onClick={() => setStep(2)}
                  className="w-full mt-5 py-3 bg-taxera-blue text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
                  data-testid="booking-continue">
                  Continue
                </button>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-3" data-testid="booking-step-2">
              <div className="bg-taxera-blue/5 rounded-xl p-3 mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-taxera-blue" />
                <span className="text-sm font-semibold text-taxera-blue">
                  {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedSlot} CET
                </span>
              </div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" required placeholder="Full name" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="booking-name" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="email" required placeholder="Work email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="booking-email" />
              </div>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" required placeholder="Company" value={form.company}
                  onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="booking-company" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="tel" placeholder="Phone (optional)" value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="booking-phone" />
              </div>
              <textarea placeholder="What would you like to discuss? (optional)" value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal resize-none"
                data-testid="booking-message" />
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setStep(1)}
                  className="px-5 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-colors"
                  data-testid="booking-back">
                  Back
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2.5 bg-taxera-blue text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="booking-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Confirm Booking'}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CalendarBooking;
